import { view } from "@risingstack/react-easy-state";

import type { Entry } from "contentful";
import { uniqBy } from "lodash-es";
import type { TypeCallToActionListSkeleton } from "../../common/types/contentful";

import { isAccordion, isAdventCalendarSlot, isRecipe, isTeaser, isUsp } from "../../common/utils/cmsListUtils";
import { getModuleVisibility } from "../../common/utils/segmentUtils";
import segmentStore from "../../stores/cms/segmentStore";
import RecipeList from "./RecipeList";
import TeaserList from "./TeaserList";
import AccordionList from "./accordion/AccordionList";
import AdventCalendar from "./advent-calendar/AdventCalendar";
import UspList from "./usp/UspList";

type CmsListProps = {
   content: Entry<TypeCallToActionListSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>;
};

const CmsList = ({ content }: CmsListProps) => {
   const isVisible = getModuleVisibility(content, segmentStore.currentCustomerProperties);

   const contentList = content.fields.contentList?.filter((content) => content?.sys?.contentType?.sys?.id !== undefined);
   const uniqueContentList = uniqBy(contentList, (entry) => entry?.sys.id);

   if (!isVisible || !contentList || uniqueContentList.length === 0) {
      return null;
   }
   const contentType = content.sys.contentType.sys.id;
   const title = content.fields.title;
   const trackingListName = content.fields.internalName;
   const id = content.sys.id;

   if (uniqueContentList.length === 0) {
      return null;
   }

   if (uniqueContentList?.every(isTeaser)) {
      const maxVisible = content.fields.maxVisible;
      const articleTeaserView = content.fields.articleTeaserView;
      return (
         <TeaserList
            teaserList={uniqueContentList}
            id={id}
            title={title}
            maxVisible={maxVisible}
            trackingListName={trackingListName}
            articleTeaserView={articleTeaserView}
         />
      );
   }

   if (uniqueContentList?.every(isRecipe)) {
      return <RecipeList recipeList={uniqueContentList} id={id} title={title} trackingListName={trackingListName} />;
   }

   if (uniqueContentList?.every(isAccordion)) {
      return <AccordionList accordionList={uniqueContentList} title={title} />;
   }

   if (uniqueContentList?.every(isUsp)) {
      return <UspList uspList={uniqueContentList} trackingListName={trackingListName} contentType={contentType} />;
   }

   if (uniqueContentList?.every(isAdventCalendarSlot)) {
      return <AdventCalendar adventCalendar={uniqueContentList} trackingListName={title} />;
   }

   return null;
};

export default view(CmsList);
